import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { Sheet } from "./Sheet";
import { Button } from "./Button";

/** Options accepted by the `confirm()` function from `useConfirm`. */
export interface ConfirmOptions {
  title: ReactNode;
  /** Body text. */
  message?: ReactNode;
  /** @default "确定" */
  confirmLabel?: string;
  /** @default "取消" */
  cancelLabel?: string;
  /** Use the danger button for the confirm action. */
  danger?: boolean;
}

export interface DialogProps extends ConfirmOptions {
  open: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  /** Disables buttons and shows a spinner on confirm. */
  busy?: boolean;
}

/** Controlled confirm dialog. Prefer the `useConfirm` hook for ad-hoc prompts. */
export function Dialog({
  open,
  title,
  message,
  confirmLabel = "确定",
  cancelLabel = "取消",
  danger = false,
  busy = false,
  onConfirm,
  onCancel,
}: DialogProps) {
  return (
    <Sheet
      open={open}
      onClose={onCancel}
      title={title}
      size="sm"
      hideClose
      footer={
        <>
          <Button variant="secondary" onClick={onCancel} disabled={busy}>
            {cancelLabel}
          </Button>
          <Button variant={danger ? "danger" : "primary"} onClick={onConfirm} loading={busy}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      {message != null && <p className="xz-dialog__message">{message}</p>}
    </Sheet>
  );
}

type ConfirmFn = (opts: ConfirmOptions) => Promise<boolean>;
const ConfirmCtx = createContext<ConfirmFn | null>(null);

/** Mounts one shared confirm dialog for the subtree. */
export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ConfirmOptions | null>(null);
  const resolver = useRef<((ok: boolean) => void) | null>(null);

  const confirm = useCallback<ConfirmFn>((opts) => {
    setState(opts);
    return new Promise<boolean>((resolve) => {
      resolver.current = resolve;
    });
  }, []);

  const settle = useCallback((ok: boolean) => {
    setState(null);
    resolver.current?.(ok);
    resolver.current = null;
  }, []);

  return (
    <ConfirmCtx.Provider value={confirm}>
      {children}
      <Dialog
        open={state !== null}
        title={state?.title ?? ""}
        message={state?.message}
        confirmLabel={state?.confirmLabel}
        cancelLabel={state?.cancelLabel}
        danger={state?.danger}
        onConfirm={() => settle(true)}
        onCancel={() => settle(false)}
      />
    </ConfirmCtx.Provider>
  );
}

/**
 * Returns `confirm(opts)` resolving to true/false.
 * Requires a `ConfirmProvider` ancestor.
 */
export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmCtx);
  if (!ctx) throw new Error("useConfirm 必须在 <ConfirmProvider> 内使用");
  return ctx;
}

/** Stable no-op-safe helper for tests/stories that need a resolved confirm. */
export const alwaysConfirm: ConfirmFn = async () => true;

export function useConfirmSafe(): ConfirmFn {
  const ctx = useContext(ConfirmCtx);
  return useMemo(() => ctx ?? alwaysConfirm, [ctx]);
}
